import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import useOpenDocument from "../../../hooks/useOpenDocument";
import { useDeleteStandardParagraphMutation, useLazyFetchStandardParagraphsQuery, useOpenStandardParagraphMutation, usePublishStandardParagraphMutation } from "../../../services/clientreports";
import { useStandardParagraphFilterContext } from "../../standardparagraphfilters/hooks/StandardParagraphFilterContext";

const limit = 25;

const useStandardParagraphsTable = (defaultSort = "createdDate") => {
    const { filter, isReady } = useStandardParagraphFilterContext();
    const [sort, setSort] = useState(defaultSort);
    const [cacheKey, setCacheKey] = useState(new Date().valueOf());
    const [isClearing, setIsClearing] = useState(false);
    const [fetchTrigger, { data, isFetching, error }] = useLazyFetchStandardParagraphsQuery();

    const { pagination, results } = data || { pagination: { page: 1, pageSize: limit, totalCount: 0, totalPages: 1 }, results: [] };
    const { page, totalCount, totalPages } = pagination;
    const hasMore = totalCount > 0 && page < totalPages;

    const clear = () => {
        if (isFetching || !isReady)
            return;

        const newCacheKey = new Date().valueOf();
        setIsClearing(true);
        fetchTrigger({
            cacheKey: newCacheKey,
            filter,
            page: 1,
            limit,
            sort
        }).unwrap().then(_ => {
            setCacheKey(newCacheKey);
            setIsClearing(false);
        });
    }

    const isRowLoaded = (index) => !hasMore || index < results.length;

    const loadMore = () => {
        if (isFetching)
            return;

        return fetchTrigger({
            cacheKey,
            filter,
            page: page + 1,
            limit,
            sort
        }).unwrap();
    }

    const sortTable = (property) => {
        let newDirection = "";
        let [sortProperty, direction] = sort.split("+");

        if (property === sortProperty)
            newDirection = direction == null ? "+DESC" : null;

        setSort(property + (newDirection ?? ""));
    }

    useEffect(clear, [filter, sort, isReady]);

    const { openDocument, isFetching: isOpening } = useOpenDocument();
    const [openTrigger] = useOpenStandardParagraphMutation();
    const [publishTrigger] = usePublishStandardParagraphMutation();
    const [deleteTrigger] = useDeleteStandardParagraphMutation();

    const onNameClick = (standardParagraphId, documentId) => {
        if (isOpening)
            return;

        openDocument(documentId);
        return openTrigger({ standardParagraphId }).unwrap()
    }

    const publishParagraph = (standardParagraphId) => {
        return toast.promise(publishTrigger({ standardParagraphId }).unwrap(), {
            loading: "Publishing Standard Paragraph Text",
            success: "Standard Paragraph Published!",
            error: "Failed to publish Standard Paragraph"
        });
    }

    const deleteParagraph = (standardParagraphId) => {
        return toast.promise(deleteTrigger({ standardParagraphId }).unwrap(), {
            loading: "Deleting Standard Paragraph",
            success: "Standard Paragraph Deleted",
            error: "Failed to delete Standard Paragraph"
        });
    }

    return {
        clear,
        data: isClearing ? Array(3).fill(undefined) : results,
        isFetching,
        isClearing,
        error,
        isRowLoaded,
        loadMore,
        currentSort: sort,
        sortTable,
        totalCount: isClearing ? 3 : totalCount,
        onNameClick,
        publishParagraph,
        deleteParagraph
    }
}

export default useStandardParagraphsTable;